import { useState } from "react";
import { useShopRevenue, type ShopRevenueRow } from "../lib/api.js";

const windows = [
  { days: undefined as number | undefined, label: "all-time" },
  { days: 30, label: "30d" },
  { days: 7, label: "7d" },
  { days: 1, label: "24h" },
];

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: n < 1 && n > 0 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n);
}

function fmtMin(min: number): string {
  if (!Number.isFinite(min) || min === 0) return "—";
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}h ${m}m`;
}

function relTime(ts: string): string {
  if (!ts) return "—";
  const ms = Date.parse(ts);
  if (Number.isNaN(ms)) return ts;
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function Bar({ pct }: { pct: number }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-800">
      <div
        className="h-full rounded-full bg-gradient-to-r from-brand-purple to-brand-orange"
        style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

function Row({ row, max }: { row: ShopRevenueRow; max: number }) {
  const pct = max > 0 ? (row.revenue / max) * 100 : 0;
  return (
    <tr className="border-b border-zinc-800/60 last:border-b-0">
      <td className="px-4 py-3">
        <div className="text-sm text-zinc-100">{row.shopName ?? "—"}</div>
        <div className="font-mono text-[11px] text-zinc-500">{row.shopId}</div>
      </td>
      <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-100">
        {fmtCurrency(row.revenue)}
      </td>
      <td className="px-4 py-3">
        <Bar pct={pct} />
      </td>
      <td className="px-4 py-3 font-mono text-xs tabular-nums text-zinc-400">
        {row.rentals}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-400">
        {fmtMin(row.avgDurationMin)}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-500">
        {relTime(row.lastRentalAt)}
      </td>
    </tr>
  );
}

export function Shops() {
  const [days, setDays] = useState<number | undefined>(undefined);
  const { data, isLoading, error } = useShopRevenue(days);
  const rows = data?.data ?? [];
  const totalRevenue = data?.meta?.totalRevenue ?? 0;
  const totalRentals = data?.meta?.totalRentals ?? 0;
  const max = rows.reduce((a, b) => Math.max(a, b.revenue), 0);

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <header className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">
            Per-shop revenue
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight brand-text">
            Where the rentals come from
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Aggregated from completed orders synthesized via{" "}
            <code className="font-mono text-xs text-zinc-400">/api/orders/backfill</code>.
            Each row is one Bajie shop; revenue is the sum of <code className="font-mono text-xs text-zinc-400">pJiesuan</code> on settled orders.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-950/60 p-1">
          {windows.map((w) => (
            <button
              key={w.label}
              onClick={() => setDays(w.days)}
              className={
                "rounded-md px-3 py-1 text-xs font-medium transition " +
                (days === w.days
                  ? "bg-brand-purple/20 text-purple-100 ring-1 ring-brand-purple/40"
                  : "text-zinc-400 hover:text-zinc-200")
              }
            >
              {w.label}
            </button>
          ))}
        </div>
      </header>

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3">
        <div className="card overflow-hidden p-5">
          <div className="absolute left-0 top-0 h-full w-0.5 bg-gradient-to-b from-brand-orange/80 to-brand-orangeDark/40" />
          <div className="stat-label">Total revenue</div>
          <div className="mt-2 stat-num text-orange-100">{fmtCurrency(totalRevenue)}</div>
        </div>
        <div className="card overflow-hidden p-5">
          <div className="absolute left-0 top-0 h-full w-0.5 bg-gradient-to-b from-brand-purple/80 to-brand-purpleDark/40" />
          <div className="stat-label">Total rentals</div>
          <div className="mt-2 stat-num text-purple-100">{totalRentals.toLocaleString()}</div>
        </div>
        <div className="card overflow-hidden p-5">
          <div className="absolute left-0 top-0 h-full w-0.5 bg-gradient-to-b from-brand-orange/80 to-brand-orangeDark/40" />
          <div className="stat-label">Shops earning</div>
          <div className="mt-2 stat-num text-orange-100">{rows.length}</div>
        </div>
      </div>

      {error && (
        <div className="card p-6 text-sm text-rose-300">
          Failed to load: {(error as Error).message}
        </div>
      )}
      {isLoading && <div className="card p-10 text-center text-sm text-zinc-500">Loading…</div>}
      {!isLoading && rows.length === 0 && (
        <div className="card p-10 text-center text-sm text-zinc-400">
          No revenue data yet. Run{" "}
          <code className="font-mono text-xs text-zinc-200">
            POST /api/orders/backfill
          </code>{" "}
          (or click "Backfill order history" on Active Rentals) to pull historical
          orders into the events table.
        </div>
      )}
      {rows.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800/60 text-left text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-medium">Shop</th>
                <th className="px-4 py-3 font-medium">Revenue</th>
                <th className="px-4 py-3 font-medium" />
                <th className="px-4 py-3 font-medium">Rentals</th>
                <th className="px-4 py-3 font-medium">Avg duration</th>
                <th className="px-4 py-3 font-medium">Last rental</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.shopId} row={r} max={max} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
